import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import CompareButton from 'src/components/elements/compare/CompareButton'
import FavoriteHeartButton from 'src/components/elements/favorite-heart/FavoriteHeartButton'
import ListingCard from 'src/components/elements/listing-card/ListingCard'
import { CreateRequestModal } from 'src/components/elements/modals'
import { saveListingNavContext } from 'src/helpers/listingNav'
import { propertyTypeLabel } from 'src/helpers/propertyTypeLabel'
import { useGetUserQuery } from 'src/redux/api'
import { MapProperty } from 'src/types/map'

interface IMapResultsGrid {
  properties: MapProperty[]
  selectedPropertyId?: string | null
  // Cabinet feeds: a horizontal carousel on phones instead of a stack of
  // full-width cards (the cabinet was 5000px long)
  mobileCarousel?: boolean
}

// Search results under the map (client feedback: the sidebar list was too
// cramped to see anything). Full-width responsive grid with readable text;
// clicking a map marker highlights the matching card and scrolls it into view
// (the list is no longer collapsed to a single item).
const MapResultsGrid: FC<IMapResultsGrid> = ({
  properties,
  selectedPropertyId,
  mobileCarousel = false,
}) => {
  const { t } = useTranslation('', { keyPrefix: 'MapPage' })
  // Hover-overlay labels live outside the MapPage namespace (step3.* etc.)
  const { t: tGlobal } = useTranslation()
  // Current user, to badge "your listing" and hide the heart on own cards.
  const { data: user } = useGetUserQuery(undefined)
  // One shared modal for the "no запит yet" heart flow — an instance per
  // card would mount dozens of heavy forms (39.8.1)
  const [openRequestModal, setOpenRequestModal] = useState(false)

  // Selecting a marker highlights and scrolls to the matching card instead of
  // collapsing the list to a single item (owner feedback: the clicked card
  // "changed position" and slid under the pane). block:'nearest' scrolls it
  // into view within the results pane without overshooting or hiding it.
  const gridRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!selectedPropertyId || !gridRef.current) return
    const card = gridRef.current.querySelector<HTMLElement>(
      `[data-property-id="${selectedPropertyId}"]`,
    )
    card?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selectedPropertyId])

  if (properties.length === 0) {
    // A silent blank list reads as "broken" on the mobile list view — say
    // what happened and turn the dead end into a lead: a saved request
    // notifies the seeker when a matching listing appears.
    return (
      <div className="py-8 flex flex-col items-center gap-3 text-center">
        <p className="text-sm text-ink-2">{t('nothing-found-filters')}</p>
        <p className="text-sm text-ink-2 max-w-[24rem]">{t('empty-cta-hint')}</p>
        {user ? (
          <button
            type="button"
            onClick={() => setOpenRequestModal(true)}
            className="h-10 px-5 rounded-xl bg-brand-strong text-sm font-bold text-white hover:bg-brand-deep transition-colors duration-200"
          >
            {t('empty-cta')}
          </button>
        ) : (
          <Link
            to="/login/create-request-without-auth"
            className="h-10 px-5 inline-flex items-center rounded-xl bg-brand-strong text-sm font-bold text-white hover:bg-brand-deep transition-colors duration-200"
          >
            {t('empty-cta')}
          </Link>
        )}
        <CreateRequestModal
          open={openRequestModal}
          handleClose={() => setOpenRequestModal(false)}
        />
      </div>
    )
  }

  return (
    <section className="flex flex-col gap-3 mt-2">
      {/* auto-fill keeps every card compact (~15-20rem) no matter how wide
          the page gets, instead of stretching a few cards across the row. */}
      <div
        ref={gridRef}
        className={
          mobileCarousel
            ? 'flex gap-3 overflow-x-auto snap-x -mx-4 px-4 scroll-px-4 pb-2 md:grid md:gap-4 md:grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] md:overflow-visible md:mx-0 md:px-0 md:pb-0'
            : 'grid gap-4 grid-cols-[repeat(auto-fill,minmax(15rem,1fr))]'
        }
      >
        {properties.map((property) => {
          const city = property.addressSection?.settlement || ''
          const district = property.addressSection?.district || ''
          const typeLabel = propertyTypeLabel(tGlobal, property.propertyType)
          const isOwn = !!user && !!property.userId && property.userId === user.id
          return (
            <ListingCard
              key={property.id}
              id={property.id}
              href={`/announcement/${property.id}`}
              // The listing page uses this ordered feed for its prev/next
              // arrows and the mobile swipe between neighbours.
              onOpen={() => saveListingNavContext(properties.map((p) => p.id))}
              images={property.images || []}
              alt={[typeLabel, city].filter(Boolean).join(', ')}
              price={property.price}
              listingType={property.listingType}
              propertyType={property.propertyType}
              parameters={property.parametersSection}
              location={[city, district].filter(Boolean).join(', ')}
              selected={selectedPropertyId === property.id}
              className={mobileCarousel ? 'shrink-0 w-[16.5rem] snap-start md:w-auto' : undefined}
              topLeft={
                (property.isPromoted || isOwn) && (
                  <>
                    {property.isPromoted && (
                      <span className="px-2 py-1 text-xs font-black text-white bg-brand-strong rounded-lg shadow">
                        {t('top-badge')}
                      </span>
                    )}
                    {isOwn && (
                      <span className="px-2 py-1 text-xs font-black text-white bg-inverse rounded-lg shadow">
                        {t('your-listing')}
                      </span>
                    )}
                  </>
                )
              }
              // Active heart toggle since 39.8.1, the compare toggle beside it
              // (B6). The heart hides on your own listing (ownerId).
              actions={
                <>
                  <CompareButton propertyId={property.id} />
                  <FavoriteHeartButton
                    propertyId={property.id}
                    isFavorite={property.isFavorite}
                    ownerId={property.userId}
                    onNeedRequest={() => setOpenRequestModal(true)}
                  />
                </>
              }
            />
          )
        })}
      </div>
      <CreateRequestModal open={openRequestModal} handleClose={() => setOpenRequestModal(false)} />
    </section>
  )
}

export default MapResultsGrid
